import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { textStyles } from '../styles/textstyle';
import { getShippingMethodName, getShippingMethodDescription, getMarketName } from '../utils/productUtils';
import CountrySelectionDialog from '../components/products/CountrySelectionDialog';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormLabel from '@mui/material/FormLabel';
import { getPaymentMethods, createOrder, createGuestOrder, getOrderDetails, getOrderSummary, calculateCartPrice } from '../api/shop';
import { PointsRedemptionResponse } from '../api/profile';
import { useAuth } from '../auth';
import { OrderDetailsResponse } from '../types/order';
import { PaymentMethod, CartCalculation, UserOrderResponse, GuestOrderResponse, GuestOrderRequest } from '../types/shop';
import { OrderDetails } from '../types/order';
import { useMarketShippingMethods, useMarkets } from '../api/product';
import { ShippingMethod } from '../types/product';
import type { Market } from '../api/product';
import { StripePayment, OrderSummary, PromoOffer, CheckoutProductList, UnifiedPointsComponent } from '../components/shop';
import { usePromoCodes } from '../hooks/usePromoCodes';

import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckoutResult from './CheckoutResult';

const MARKET_ID_KEY = 'selected_market_id';

// Hong Kong regions for shipping
const HONG_KONG_REGIONS = [
  { value: 'new_territories', label: 'newTerritories' },
  { value: 'kowloon', label: 'kowloon' },
  { value: 'hong_kong_island', label: 'hongKongIsland' }
];

// Validation schema using yup
const createSchema = (t: (key: string) => string) => yup.object().shape({
  firstName: yup.string().required(t('checkout.firstNameRequired')),
  lastName: yup.string().required(t('checkout.lastNameRequired')),
  address: yup.string().required(t('checkout.addressRequired')),
  city: yup.string().required(t('checkout.cityRequired')),
  region: yup.string().optional(),
  mobile: yup.string().required(t('checkout.mobileRequired')),
  email: yup.string().email(t('checkout.invalidEmail')).required(t('checkout.emailRequired')),
  shippingMethod: yup.string().required(t('checkout.shippingMethodRequired')),
  paymentMethod: yup.string().required(t('checkout.paymentMethodRequired')),
  countryCode: yup.string().required(t('checkout.countryRequired')),
}) as yup.ObjectSchema<FormValues>;

// Type for form values
type FormValues = {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  region?: string;
  mobile: string;
  email: string;
  shippingMethod: string;
  paymentMethod: string;
  countryCode: string;
};

const Checkout = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [cartCalculation, setCartCalculation] = useState<CartCalculation | null>(location.state?.cartCalculation as CartCalculation);
  const [countrySelectionDialogOpen, setCountrySelectionDialogOpen] =
    useState(false);
  const [selectedCountry, setSelectedCountry] = useState<{
    code: string;
    name: string;
    marketId: number;
    marketName: string;
  } | null>(null);
  const [phoneCountry, setPhoneCountry] = useState<'HK' | 'US' | 'JP' | 'CN' | 'GB' | 'VN' | 'AE' | 'MO' | 'BD'>('HK');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [stripeError, setStripeError] = useState<string | null>(null);
  
  // New state for payment flow
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [stripePaymentHandler, setStripePaymentHandler] = useState<((orderId?: number) => Promise<void>) | null>(null);
  
  // Memoized setIsProcessing to prevent infinite re-renders
  const memoizedSetIsProcessing = useCallback((processing: boolean) => {
    setIsProcessing(processing);
  }, []);
  
  // New state for checkout result
  const [checkoutStatus, setCheckoutStatus] = useState<'processing' | 'success' | 'fail' | null>(null);
  const [orderResponse, setOrderResponse] = useState<GuestOrderResponse | UserOrderResponse | null>(null);
  const [orderRequest, setOrderRequest] = useState<GuestOrderRequest | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | OrderDetailsResponse | null>(null);

  // Get marketId from sessionStorage (set by ProductDetails or Cart)
  const getMarketId = () => sessionStorage.getItem(MARKET_ID_KEY) || '';

  // Get markets data to find Hong Kong market
  const { marketsData } = useMarkets();

  // Promo code management
  const {
    newPromoCode,
    setNewPromoCode,
    isApplying,
    removingCode,
    error,
    successMessage,
    applyPromoCode,
    removePromoCode,
    clearPromoCodes,
  } = usePromoCodes({
    cartCalculation,
    onCartUpdate: (newCalculation) => {
      // Update the local cart calculation state
      setCartCalculation(newCalculation);
    },
    isAuthenticated,
  });



  const display = cartCalculation?.display || {};
  const subtotal = cartCalculation?.breakdown?.subtotal || 0;
  const currency = cartCalculation?.currency || 'HK$';

  // Reusable style for all TextFields (border and placeholder color)
  const textFieldSx = {
    '& input::placeholder': { color: '#3D3C3C', opacity: 1 },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#E1DACA',
        borderWidth: 1,
      },
      '&:hover fieldset': {
        borderColor: '#E1DACA',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#E1DACA',
      },
    },
  };

  // useForm setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
    setError,
  } = useForm<FormValues>({
    resolver: yupResolver(createSchema(t)),
    mode: 'onChange',
  });



  // Prefill email when user is authenticated
  React.useEffect(() => {
    if (isAuthenticated && user?.email) {
      setValue('email', user.email);
    }
  }, [isAuthenticated, user?.email, setValue]);

  // Set default region for Hong Kong market AND Hong Kong country only
  React.useEffect(() => {
    const isHongKongMarket = selectedCountry?.marketId && marketsData && 
      marketsData.find(market => market.id === selectedCountry.marketId)?.countriesWithNames.some(country => country.code === 'HK');
    const isHongKongCountry = selectedCountry?.code === 'HK';
    const shouldSetRegion = isHongKongMarket && isHongKongCountry;
    
    if (shouldSetRegion) {
      setValue('region', HONG_KONG_REGIONS[0].value);
    } else if (selectedCountry?.code) {
      // Clear region value for non-Hong Kong markets or non-Hong Kong countries
      setValue('region', '');
    }
  }, [selectedCountry, marketsData, setValue]);

  // Handle phone number change
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePhoneChange = (value: string, info: any) => {
    console.log('Phone changed:', value, info);
    setValue('mobile', value);
  };

  // Initialize selected country from sessionStorage
  React.useEffect(() => {
    if (!marketsData || marketsData.length === 0) return;
    
    const marketId = getMarketId();
    
    if (marketId) {
      // Find the market from API data
      const existingMarket = marketsData.find((market: Market) => market.id === parseInt(marketId));
      const defaultCountry = { 
        code: 'HK', 
        name: existingMarket?.name || 'Hong Kong', 
        marketId: parseInt(marketId), 
        marketName: existingMarket?.name || 'hong kong' 
      };
      setSelectedCountry(defaultCountry);
      setValue('countryCode', defaultCountry.code);
      setPhoneCountry('HK');
    } else {
      // Find Hong Kong market from API data
      const hongKongMarket = marketsData.find((market: Market) => 
        market.countriesWithNames.some(country => country.code === 'HK')
      );
      
      if (hongKongMarket) {
        const defaultCountry = { 
          code: 'HK', 
          name: hongKongMarket.name, 
          marketId: hongKongMarket.id, 
          marketName: hongKongMarket.name 
        };
        sessionStorage.setItem(MARKET_ID_KEY, hongKongMarket.id.toString());
        setSelectedCountry(defaultCountry);
        setValue('countryCode', defaultCountry.code);
        setPhoneCountry('HK');
      }
    }
  }, [marketsData, setValue]);

  // Fetch payment methods from API
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoadingPaymentMethods(true);
        const methods = await getPaymentMethods();
        setPaymentMethods(methods);
      } catch (error) {
        console.error('Failed to fetch payment methods:', error);
      } finally {
        setLoadingPaymentMethods(false);
      }
    };

    fetchPaymentMethods();
  }, []);

  // Get shipping methods from API
  const { shippingMethodsData, shippingMethodsIsLoading } = useMarketShippingMethods();

  // Get all shipping methods for selected country's market ID
  const allShippingMethods = React.useMemo(() => {
    if (!shippingMethodsData || !selectedCountry) {
      return [];
    }
    
    const filteredMethods = shippingMethodsData.filter((method: ShippingMethod) => {
      // Try both strict and loose equality to handle potential type mismatches
      const strictMatch = method.marketId === selectedCountry.marketId;
      const looseMatch = method.marketId == selectedCountry.marketId;
      const matches = (strictMatch || looseMatch) && method.isActive;
      return matches;
    });
    
    // TEMPORARY FIX: If no methods found for exact marketId, show any active methods
    if (filteredMethods.length === 0 && shippingMethodsData.length > 0) {
      const fallbackMethods = shippingMethodsData.filter((method: ShippingMethod) => method.isActive);
      return fallbackMethods;
    }
    
    return filteredMethods;
  }, [shippingMethodsData, selectedCountry]);

  // Check which shipping methods are available based on order amount
  const availableShippingMethods = React.useMemo(() => {
    const filtered = allShippingMethods.filter((method: ShippingMethod) => {
      // If there's a minimum amount requirement, check if order amount meets it
      if (method.minAmount !== null && method.minAmount > 0) {
        return subtotal >= method.minAmount;
      }
      // If no minimum amount, method is available
      return true;
    });
    
    return filtered;
  }, [allShippingMethods, subtotal]);


  // Watch shipping method changes
  const watchedShippingMethod = watch('shippingMethod');
  const [lastProcessedShippingMethod, setLastProcessedShippingMethod] = useState<string | null>(null);
  
  // Reward points state
  const [appliedPoints, setAppliedPoints] = useState<PointsRedemptionResponse | null>(null);

  // Fetch available points for authenticated users
  // useEffect(() => {
  //   const fetchAvailablePoints = async () => {
  //     if (isAuthenticated) {
  //       try {
  //         const pointsData = await getRewardPointsSummary();
  //         setAvailablePoints(pointsData.availablePoints || 0);
  //       } catch (error) {
  //         console.error('Failed to fetch available points:', error);
  //         setAvailablePoints(0);
  //       }
  //     } else {
  //       setAvailablePoints(0);
  //     }
  //   };
  //
  //   fetchAvailablePoints();
  // }, [isAuthenticated]);

  // Update available points when applied points change
  // useEffect(() => {
  //   if (isAuthenticated && appliedPoints) {
  //     // Refresh points data when points are applied/removed
  //     const refreshPoints = async () => {
  //       try {
  //         const pointsData = await getRewardPointsSummary();
  //         setAvailablePoints(pointsData.availablePoints || 0);
  //       } catch (error) {
  //         console.error('Failed to refresh available points:', error);
  //       }
  //     };
  //     refreshPoints();
  //   }
  // }, [isAuthenticated, appliedPoints]);

  // Function to recalculate order when points are applied/removed
  const recalculateOrderWithPoints = useCallback(async () => {
    if (!cartCalculation || !watchedShippingMethod) return;

    try {
      let result: CartCalculation;
      
      if (isAuthenticated) {
        // For authenticated users, use order-summary API
        result = await getOrderSummary({
          cartItems: [], // Always empty for authenticated users
          shippingMethodId: watchedShippingMethod,
          marketId: getMarketId(),
          promoCodes: cartCalculation?.validatedDiscounts?.appliedDiscounts
            ?.filter(discount => !discount.isAutomatic)
            ?.map(discount => discount.code) || [],
          pointsToUse: appliedPoints?.pointsToRedeem || 0,
        });
      } else {
        // For guest users, use calculate-price API
        const localCart = JSON.parse(sessionStorage.getItem('cart_items') || '[]');
        const items = localCart.map((item: { id: number; quantity: number; variantId?: number; selectedOptions?: { [key: string]: string } }) => ({
          productId: item.id,
          quantity: item.quantity,
          variantId: item.variantId,
          selectedOptions: item.selectedOptions,
        }));
        
        if (!items.length) return;
        
        result = await calculateCartPrice({
          cartItems: items,
          shippingMethodId: watchedShippingMethod,
          marketId: getMarketId(),
          promoCodes: cartCalculation?.validatedDiscounts?.appliedDiscounts
            ?.filter(discount => !discount.isAutomatic)
            ?.map(discount => discount.code) || [],
          pointsToUse: appliedPoints?.pointsToRedeem || 0,
        });
      }

      // Update the local state
      setCartCalculation(result);
    } catch (error) {
      console.error('Error recalculating order with points:', error);
    }
  }, [cartCalculation, watchedShippingMethod, isAuthenticated, getMarketId, appliedPoints]);

  // Function to recalculate order with new shipping method
  const recalculateOrderWithShippingMethod = useCallback(async (shippingMethodId: string) => {
    if (!cartCalculation || lastProcessedShippingMethod === shippingMethodId) return;

    try {
      let result: CartCalculation;
      
      if (isAuthenticated) {
        // For authenticated users, use order-summary API
        result = await getOrderSummary({
          cartItems: [], // Always empty for authenticated users
          shippingMethodId: shippingMethodId,
          marketId: getMarketId(),
          promoCodes: cartCalculation?.validatedDiscounts?.appliedDiscounts
            ?.filter(discount => !discount.isAutomatic)
            ?.map(discount => discount.code) || [],
          pointsToUse: appliedPoints?.pointsToRedeem || 0,
        });
      } else {
        // For guest users, use calculate-price API
        const localCart = JSON.parse(sessionStorage.getItem('cart_items') || '[]');
        const items = localCart.map((item: { id: number; quantity: number; variantId?: number; selectedOptions?: { [key: string]: string } }) => ({
          productId: item.id,
          quantity: item.quantity,
          variantId: item.variantId,
          selectedOptions: item.selectedOptions,
        }));
        
        if (!items.length) return;
        
        result = await calculateCartPrice({
          cartItems: items,
          shippingMethodId: shippingMethodId,
          marketId: getMarketId(),
          promoCodes: cartCalculation?.validatedDiscounts?.appliedDiscounts
            ?.filter(discount => !discount.isAutomatic)
            ?.map(discount => discount.code) || [],
          pointsToUse: appliedPoints?.pointsToRedeem || 0,
        });
      }

      // Update both the local state and the location state
      setCartCalculation(result);
      navigate('/checkout', { 
        state: { 
          cartCalculation: result,
          ...location.state 
        },
        replace: true 
      });
      
      // Mark this shipping method as processed
      setLastProcessedShippingMethod(shippingMethodId);
    } catch (error) {
      console.error('Failed to recalculate order with new shipping method:', error);
    }
  }, [isAuthenticated, getMarketId, navigate, location.state, lastProcessedShippingMethod]);

  // Watch for shipping method changes and recalculate
  useEffect(() => {
    if (watchedShippingMethod && watchedShippingMethod !== lastProcessedShippingMethod) {
      recalculateOrderWithShippingMethod(watchedShippingMethod);
    }
  }, [watchedShippingMethod, recalculateOrderWithShippingMethod, lastProcessedShippingMethod]);

  // Auto-select free shipping method when available
  useEffect(() => {
    if (availableShippingMethods.length > 0 && !watchedShippingMethod) {
      // Find free shipping method (price === 0)
      const freeShippingMethod = availableShippingMethods.find((method: ShippingMethod) => method.price === 0);
      
      if (freeShippingMethod) {
        // Auto-select free shipping method
        setValue('shippingMethod', freeShippingMethod.id.toString());
        // Trigger recalculation for the auto-selected method
        setTimeout(() => {
          recalculateOrderWithShippingMethod(freeShippingMethod.id.toString());
        }, 100);
      } else {
        // If no free shipping, select the first available method
        setValue('shippingMethod', availableShippingMethods[0].id.toString());
        // Trigger recalculation for the auto-selected method
        setTimeout(() => {
          recalculateOrderWithShippingMethod(availableShippingMethods[0].id.toString());
        }, 100);
      }
    }
  }, [availableShippingMethods, watchedShippingMethod, setValue, recalculateOrderWithShippingMethod]);

  // Ensure initial cart calculation includes shipping method when component mounts
  useEffect(() => {
    if (cartCalculation && availableShippingMethods.length > 0 && watchedShippingMethod && !lastProcessedShippingMethod) {
      // If we have a cart calculation but no shipping method has been processed yet,
      // trigger a recalculation with the current selected shipping method
      recalculateOrderWithShippingMethod(watchedShippingMethod);
    }
  }, [cartCalculation, availableShippingMethods, watchedShippingMethod, lastProcessedShippingMethod, recalculateOrderWithShippingMethod]);

  // Ensure we have a proper cart calculation with shipping when component mounts
  useEffect(() => {
    if (cartCalculation && availableShippingMethods.length > 0 && !watchedShippingMethod) {
      // If we have a cart calculation but no shipping method selected yet,
      // wait for the auto-selection to happen and then ensure we calculate
    }
  }, [cartCalculation, availableShippingMethods, watchedShippingMethod]);

  // Create order function
  const createOrderRequest = async (formData: FormValues) => {
      if (!cartCalculation) {
        throw new Error('Cart calculation is not available');
      }
      
      // Prepare cart items from cartCalculation
      const cartItems = cartCalculation.items?.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        variantId: item.variantId,
        selectedOptions: item.selectedOptions,
      })) || [];

      // Check if the selected market is Hong Kong AND selected country is Hong Kong
      const isHongKongMarket = selectedCountry?.marketId && marketsData && 
        marketsData.find(market => market.id === selectedCountry.marketId)?.countriesWithNames.some(country => country.code === 'HK');
      const isHongKongCountry = selectedCountry?.code === 'HK';
      const shouldIncludeRegion = isHongKongMarket && isHongKongCountry;

    // Prepare the order request
    const orderRequestData: GuestOrderRequest = {
        cartItems,
        shippingAddress: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
          apartment: '', // Optional field from form
        city: formData.city,
        state: shouldIncludeRegion ? (formData.region || '') : '', // Only include region for Hong Kong market AND Hong Kong country
        country: formData.countryCode,
        zipCode: '', // Empty for Hong Kong
        phone: formData.mobile,
        email: formData.email,
        },
        billingAddress: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
          apartment: '', // Optional field from form
        city: formData.city,
        state: shouldIncludeRegion ? (formData.region || '') : '', // Only include region for Hong Kong market AND Hong Kong country
        country: formData.countryCode,
        zipCode: '', // Empty for Hong Kong
        phone: formData.mobile,
        email: formData.email,
      },
      paymentMethodId: formData.paymentMethod,
      shippingMethodId: formData.shippingMethod,
        promoCodes: cartCalculation?.validatedDiscounts?.appliedDiscounts
          ?.filter(discount => !discount.isAutomatic)
          ?.map(discount => discount.code) || [],
        pointsToRedeem: appliedPoints?.pointsToRedeem || 0,
        customerInfo: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.mobile,
          marketingConsent: false, // TODO: Add marketing consent checkbox
        },
        marketId: getMarketId(),

    };

    try {
      setIsProcessing(true);
      setOrderRequest(orderRequestData);
      
      let orderResponseData: UserOrderResponse | GuestOrderResponse;
      
      if (isAuthenticated) {
        orderResponseData = await createOrder(orderRequestData);
      } else {
        orderResponseData = await createGuestOrder(orderRequestData);
      }
      
      setOrderResponse(orderResponseData);
      const newOrderId = 'order' in orderResponseData ? orderResponseData.order.id : parseInt(orderResponseData.orderId);
      setOrderId(newOrderId);
      
      // Update the orderId in window object for StripePayment to access
      if (typeof window !== 'undefined') {
        (window as unknown as Record<string, unknown>).currentOrderId = newOrderId;
      }
      
      // Don't show success immediately - wait for payment completion
      // For non-Stripe payments, success will be shown after order creation
      // For Stripe payments, success will be shown after payment completion
      
      return orderResponseData;
    } catch (error) {
      console.error('Failed to create order:', error);
      setCheckoutStatus('fail');
      throw error;
    } finally {
      // Don't stop loading here - let the calling function manage it
      // setIsProcessing(false);
    }
  };

  // Handle payment success
  const handlePaymentSuccess = useCallback(async (orderId: number) => {
    try {
      // Only fetch order details for authenticated users
      if (isAuthenticated) {
        // Fetch the complete order details after payment success
        const details = await getOrderDetails(orderId);
        setOrderDetails(details);
      }
      setCheckoutStatus('success');
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      // Fallback to success status even if order details fetch fails
      setCheckoutStatus('success');
    }
  }, [isAuthenticated]);

  // Handle payment error
  const handlePaymentError = useCallback((error: string) => {
    setStripeError(error);
    setCheckoutStatus('fail');
    console.error('Payment error:', error);
  }, []);

  // Handle retry
  const handleRetry = useCallback(() => {
    setCheckoutStatus(null);
    setOrderResponse(null);
    setOrderRequest(null);
    setOrderDetails(null);
    setOrderId(null);
    setStripeError(null);
  }, []);

  // Handle back to home
  const handleBackToHome = useCallback(() => {
    window.location.href = '/';
  }, [navigate]);

  // Handle card completion
  const handleCardComplete = useCallback((complete: boolean) => {
    setCardComplete(complete);
  }, []);

  // Handle cardholder name change
  const handleCardholderNameChange = useCallback((name: string) => {
    setCardholderName(name);
  }, []);

  // Handle payment handler ready
  const handlePaymentReady = useCallback((paymentHandler: (orderId?: number) => Promise<void>) => {
    console.log('Payment handler ready');
    setStripePaymentHandler(() => paymentHandler);
  }, []);

  // Handle place order and pay
  const handlePlaceOrderAndPay = async () => {
    try {
      const formData = watch();
      console.log('formData', formData);
      
      // Validate form
      const isValid = await new Promise<boolean>((resolve) => {
        handleSubmit(() => resolve(true), () => resolve(false))();
      });
      console.log('isValid', isValid);
      
      if (!isValid) {
        return;
      }

      // Create order first and wait for it to complete
      const orderResponse = await createOrderRequest(formData);
      
      // Get orderId from the response
      const newOrderId = 'order' in orderResponse ? orderResponse.order.id : parseInt(orderResponse.orderId);
      
      // Check if Stripe payment is selected
      const selectedPaymentMethod = paymentMethods.find(method => 
        method.id.toString() === formData.paymentMethod
      );
      const isStripePayment = selectedPaymentMethod?.name.toLowerCase().includes('stripe');
      
      if (isStripePayment && stripePaymentHandler) {
        // For Stripe payments, trigger payment after order is created
        console.log('Order created, now triggering payment...');
        await stripePaymentHandler(newOrderId);
      } else {
        // For non-Stripe payments, keep loading until order details are fetched
        try {
          // Only fetch order details for authenticated users
          if (isAuthenticated) {
            // Fetch the complete order details
            const details = await getOrderDetails(newOrderId);
            setOrderDetails(details);
          }
        } catch (error) {
          console.error('Failed to fetch order details:', error);
        }
        setCheckoutStatus('success');
      }
    } catch (error) {
      console.error('Failed to place order:', error);
      setStripeError('Failed to create order. Please try again.');
    } finally {
      // Stop loading after all operations are complete
      setIsProcessing(false);
    }
  };

  // Check if Stripe payment is selected
  const isStripeSelected = watch('paymentMethod') && paymentMethods.find(method => 
    method.id.toString() === watch('paymentMethod') && 
    method.name.toLowerCase().includes('stripe')
  );

  // Removed automatic payment trigger - now handled directly in handlePlaceOrderAndPay

  const onSubmit: SubmitHandler<FormValues> = async () => {
    console.log('onSubmit called');
    
    // Custom validation for region field based on market AND country
    const isHongKongMarket = selectedCountry?.marketId && marketsData && 
      marketsData.find(market => market.id === selectedCountry.marketId)?.countriesWithNames.some(country => country.code === 'HK');
    const isHongKongCountry = selectedCountry?.code === 'HK';
    const shouldRequireRegion = isHongKongMarket && isHongKongCountry;

    console.log('shouldRequireRegion', shouldRequireRegion);
    
    if (shouldRequireRegion && !watch('region')) {
      setError('region', { 
        type: 'manual', 
        message: t('checkout.regionRequired') 
      });
      console.log('shouldRequireRegion', 'return');
      return;
    }
    
    // This is now handled by handlePlaceOrderAndPay
    await handlePlaceOrderAndPay();
  };

  // Show CheckoutResult if we have a status
  if (checkoutStatus) {
    return (
      <CheckoutResult
        status={checkoutStatus}
        orderResponse={orderResponse}
        orderRequest={orderRequest}
        orderDetails={orderDetails}
        onBackToHome={handleBackToHome}
        onRetry={handleRetry}
        embedded={true}
      />
    );
  }

  // Fallback if no cartCalculation is passed
  if (!cartCalculation) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h6">
          No cart data found. Please return to cart.
        </Typography>
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          onClick={() => navigate('/cart')}
        >
          Back to Cart
        </Button>
      </Box>
    );
  }

  const handleSelectCountry = (countryCode: string, countryName: string, marketId: number, marketName: string) => {
    setSelectedCountry({ code: countryCode, name: countryName, marketId, marketName });
    // Update sessionStorage with the new market ID
    sessionStorage.setItem(MARKET_ID_KEY, marketId.toString());
    // Set the country code in the form
    setValue('countryCode', countryCode);
    // Update phone country to match shipping country (only if it's in our allowed list)
    if (['HK', 'US', 'JP', 'CN', 'GB', 'VN', 'AE', 'MO', 'BD'].includes(countryCode)) {
      setPhoneCountry(countryCode as 'HK' | 'US' | 'JP' | 'CN' | 'GB' | 'VN' | 'AE' | 'MO' | 'BD');
    }
  };

  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      gap={4}
      maxWidth="lg"
      mx="auto"
      p={3}
    >
      {/* Left: Shipping, Contact, Payment */}
      <Box flex={2} minWidth={0}>
        <Typography variant="h5" fontWeight={700} mb={2}>
          {t('checkout.placeOrder')}
        </Typography>
        {/* Shipping Address */}
        <Box display="flex" alignItems="center" mb={1}>
          <Typography sx={textStyles.h6Small}>
            {t('checkout.shippingInformation')}
          </Typography>
        </Box>
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            p: 1.5,
            backgroundColor: '#FCFAF6',
            border: '1px solid #E1DACA',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <img
                src="/icons/ic_shipping.svg"
                alt="Shipping"
                style={{ width: 20, height: 20 }}
              />
              <Typography fontWeight={500}>
                {t('checkout.shippingInformation')} {selectedCountry ? (() => {
                  const market = marketsData.find((m: Market) => m.id === selectedCountry.marketId);
                  return market ? getMarketName(market, i18n.language) : selectedCountry.name;
                })() : t('cart.hongKong')}
              </Typography>
            </Box>
            <Button
              sx={{
                color: '#17110D',
                textDecoration: 'underline',
                fontSize: '14px',
              }}
              variant="text"
              size="small"
              onClick={() => setCountrySelectionDialogOpen(true)}
            >
              {t('cart.change')}
            </Button>
          </Box>
        </Paper>
        {/* Shipping Address Form */}
        <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Box display="flex" gap={2} mb={1}>
            <TextField
              placeholder={t('checkout.firstName')}
              fullWidth
              size="small"
              variant="outlined"
              sx={textFieldSx}
              error={!!errors.firstName}
              helperText={errors.firstName?.message ? t(errors.firstName.message) : ''}
              {...register('firstName')}
            />
            <TextField
              placeholder={t('checkout.lastName')}
              fullWidth
              size="small"
              variant="outlined"
              sx={textFieldSx}
              error={!!errors.lastName}
              helperText={errors.lastName?.message ? t(errors.lastName.message) : ''}
              {...register('lastName')}
            />
          </Box>
          <Typography fontSize={12} color="#3D3C3C" mb={2}>
            {t('checkout.deliveryWarning')}
          </Typography>
          <TextField
            placeholder={t('checkout.address')}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <span style={{ marginRight: 8 }}>
                  <img
                    src="/icons/ic_search_brown.svg"
                    alt="Address"
                    style={{
                      width: 18,
                      height: 18,
                      opacity: 0.6,
                      marginTop: '6px',
                    }}
                  />
                </span>
              ),
            }}
            variant="outlined"
            sx={{ mb: 2, ...textFieldSx }}
            error={!!errors.address}
            helperText={errors.address?.message ? t(errors.address.message) : ''}
            {...register('address')}
          />
          <TextField
            placeholder="Apt/Suite/Other (optional)"
            fullWidth
            size="small"
            variant="outlined"
            sx={{ mb: 2, ...textFieldSx }}
          />
          <TextField
            placeholder={t('checkout.city')}
            fullWidth
            size="small"
            variant="outlined"
            sx={{ mb: 2, ...textFieldSx }}
            error={!!errors.city}
                          helperText={errors.city?.message ? t(errors.city.message) : ''}
            {...register('city')}
          />
          {/* Show region field for Hong Kong market AND Hong Kong country */}
          {selectedCountry?.marketId && marketsData && 
           marketsData.find(market => market.id === selectedCountry.marketId)?.countriesWithNames.some(country => country.code === 'HK') && 
           selectedCountry.code === 'HK' && (
            <Controller
              name="region"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <FormControl fullWidth size="small" error={!!errors.region} sx={{ mb: 2 }}>
                  <Select
                    {...field}
                    value={field.value || HONG_KONG_REGIONS[0].value}
                    onChange={(e) => {
                      console.log('Region changed:', e.target.value);
                      field.onChange(e);
                    }}
                    sx={textFieldSx}
                  >
                    {HONG_KONG_REGIONS.map((region) => (
                      <MenuItem key={region.value} value={region.value}>
                        {t(`checkout.${region.label}`)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.region && (
                    <Typography color="error" fontSize={12} mt={0.5}>
                      {errors.region.message ? t(errors.region.message) : ''}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          )}

          <FormControlLabel
            control={<Checkbox sx={{ color: '#3D3C3C' }} />}
            label={t('checkout.makeDefaultAddress')}
            sx={{ mb: 2, color: '#3D3C3C' }}
          />
          <Button
            variant="outlined"
            fullWidth
            sx={{
              color: '#b48b6a',
              borderColor: '#b48b6a',
              fontWeight: 700,
              fontSize: 16,
              py: 1,
              mb: 1,
              background: 'transparent',
            }}
          >
            {t('checkout.saveAndContinue')}
          </Button>
        </Paper>
        {/* Contact Information */}
        <Typography sx={textStyles.h6} fontWeight={700} mb={0.5}>
          {t('checkout.contactInformation')}
        </Typography>
        <Typography fontSize={14} color="text.secondary" mb={2}>
          {t('checkout.contactInformationDescription')}
        </Typography>
        <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Controller
            name="mobile"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <MuiTelInput
                {...field}
                fullWidth
                size="small"
                variant="outlined"
                sx={{
                  mb: 2,
                  ...textFieldSx,
                  '& .MuiTelInput-flagButton': {
                    borderColor: '#E1DACA',
                  },
                  '& .MuiTelInput-flagButton:hover': {
                    borderColor: '#E1DACA',
                  },
                  '& .MuiTelInput-flagButton.Mui-focused': {
                    borderColor: '#E1DACA',
                  },
                }}
                error={!!errors.mobile}
                helperText={errors.mobile?.message ? t(errors.mobile.message) : ''}
                defaultCountry={phoneCountry}
                preferredCountries={['HK', 'US', 'JP', 'CN', 'GB', 'VN', 'AE', 'MO', 'BD']}
                onlyCountries={['HK', 'US', 'JP', 'CN', 'GB', 'VN', 'AE', 'MO', 'BD']}
                forceCallingCode={true}
                disableFormatting={false}
                placeholder={t('checkout.mobilePlaceholder')}
                onChange={handlePhoneChange}
              />
            )}
          />
          <TextField
            placeholder={t('checkout.emailPlaceholder')}
            fullWidth
            size="small"
            variant="outlined"
            sx={textFieldSx}
            error={!!errors.email}
                            helperText={errors.email?.message ? t(errors.email.message) : ''}
            {...register('email')}
          />
        </Paper>
        {/* Shipping Method Section */}
        <FormControl component="fieldset" fullWidth sx={{ mb: 4 }} error={!!errors.shippingMethod}>
          <FormLabel component="legend" sx={textStyles.h6}>
            {t('checkout.shippingMethod')}
          </FormLabel>
          <Typography fontSize={14} color="text.secondary" mb={2}>
                      {t('checkout.selectPreferredShippingMethod')}
        </Typography>
          {shippingMethodsIsLoading ? (
            <Typography color="text.secondary" mt={2}>
              {t('checkout.loadingShippingMethods')}
            </Typography>
          ) : availableShippingMethods.length === 0 ? (
            <Box>
              <Typography color="error" mt={2}>
                {t('checkout.noShippingMethodsAvailable')}
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={() => window.location.reload()}
                sx={{ mt: 1 }}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <Controller
              name="shippingMethod"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <RadioGroup {...field} sx={{ width: '100%' }}>
                  {availableShippingMethods.map((method, index) => (
                      <React.Fragment key={method.id}>
                        {index > 0 && <Divider sx={{ my: 1, borderColor: '#E1DACA' }} />}
                      <Box display="flex" alignItems="center" justifyContent="space-between" px={1} mt={index === 0 ? 2 : 0}>
                          <FormControlLabel
                            value={method.id.toString()}
                          control={<Radio sx={{ color: '#E1DACA', '&.Mui-checked': { color: '#17110D' } }} />}
                            label={
                              <Box>
                              <Typography sx={textStyles.priceLarge} fontWeight={500} color="#17110D">
                                  {getShippingMethodName(method, i18n.language)}
                                </Typography>
                                {getShippingMethodDescription(method, i18n.language) && (
                                <Typography color="#3D3C3C" fontSize={14} sx={textStyles.bodyTiny}>
                                    {getShippingMethodDescription(method, i18n.language)}
                                  </Typography>
                                )}
                              <Typography color="#3D3C3C" fontSize={14} sx={textStyles.bodyTiny}>
                                {'Standard delivery'}
                              </Typography>
                              </Box>
                            }
                            sx={{ flex: 1, m: 0 }}
                          />
                        <Typography sx={textStyles.priceLarge} fontWeight={500} color="#17110D">
                            {method.price === 0 ? 'Free' : `${currency}${method.price.toFixed(2)}`}
                          </Typography>
                        </Box>
                    </React.Fragment>
                  ))}
                </RadioGroup>
              )}
            />
          )}
          {errors.shippingMethod && (
            <Typography color="error" fontSize={14} mt={1} ml={1}>
              {errors.shippingMethod.message ? t(errors.shippingMethod.message) : ''}
            </Typography>
          )}
        </FormControl>
        {/* Payment Method Section */}
        <FormControl component="fieldset" fullWidth sx={{ mb: 4 }} error={!!errors.paymentMethod}>
          <FormLabel component="legend" sx={textStyles.h6}>
                            {t('checkout.paymentMethod')}
          </FormLabel>
          {loadingPaymentMethods ? (
            <Typography color="text.secondary" mt={2}>
              {t('checkout.loadingPaymentMethods')}
            </Typography>
          ) : (
            <Controller
              name="paymentMethod"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <RadioGroup {...field} sx={{ width: '100%' }}>
                  {paymentMethods
                    .filter(method => method.isActive)
                    .map((method, index) => (
                      <React.Fragment key={method.id}>
                        {index > 0 && <Divider sx={{ my: 1, borderColor: '#E1DACA' }} />}
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1} mt={index === 0 ? 2 : 0}>
                          <FormControlLabel
                            value={method.id.toString()}
                            control={<Radio sx={{ color: '#E1DACA', '&.Mui-checked': { color: '#17110D' } }} />}
                            label={
                              <Box>
                                <Typography sx={textStyles.priceLarge} fontWeight={500} color="#17110D">
                                  {method.name}
                                </Typography>
                                {method.description && (
                                  <Typography color="#3D3C3C" fontSize={14} sx={textStyles.bodyTiny}>
                                    {method.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{ flex: 1, m: 0 }}
                          />
                          {/* You can add payment method icons here based on method.name if needed */}
                        </Box>
                      </React.Fragment>
                    ))}
                </RadioGroup>
              )}
            />
          )}
          {errors.paymentMethod && (
            <Typography color="error" fontSize={14} mt={1} ml={1}>
              {errors.paymentMethod.message ? t(errors.paymentMethod.message) : ''}
            </Typography>
          )}
        </FormControl>
        
        {/* Stripe Payment Component - Show when Stripe is selected */}
        {isStripeSelected && (
          <Box sx={{ mb: 4 }}>
            <StripePayment 
              onError={handlePaymentError}
              onCardComplete={handleCardComplete}
              onCardholderNameChange={handleCardholderNameChange}
              cardholderName={cardholderName}
              onPaymentSuccess={handlePaymentSuccess}
              orderId={orderId}
              isProcessing={isProcessing}
              setIsProcessing={memoizedSetIsProcessing}
              onPaymentReady={handlePaymentReady}
            />
            {stripeError && (
              <Typography color="error" fontSize={14} mt={1} ml={1}>
                {stripeError}
                        </Typography>
                      )}
                    </Box>
              )}
        
        {/* Product List */}
        <CheckoutProductList cartCalculation={cartCalculation} />
      </Box>
      {/* Right: Promo & Offers and Order Summary */}
      <Box flex={1} minWidth={320}>
        {/* Promo & Offers */}
        <PromoOffer 
          display={display}
          newPromoCode={newPromoCode}
          setNewPromoCode={setNewPromoCode}
          isApplying={isApplying}
          removingCode={removingCode}
          error={error}
          successMessage={successMessage}
          onApplyPromoCode={applyPromoCode}
          onRemovePromoCode={removePromoCode}
          onClearPromoCodes={clearPromoCodes}
          ignoredDiscounts={cartCalculation?.validatedDiscounts?.ignoredDiscounts || []}
          appliedDiscounts={cartCalculation?.validatedDiscounts?.appliedDiscounts || []}
        />
        
        {/* Unified Points Component */}
        <UnifiedPointsComponent
          isAuthenticated={isAuthenticated}
          onPointsApplied={setAppliedPoints}
          appliedPoints={appliedPoints}
          onOrderRecalculation={() => {
            // Trigger order recalculation when points are applied/removed
            recalculateOrderWithPoints();
          }}
        />
        
        {/* Order Summary */}
        <OrderSummary 
          cartCalculation={cartCalculation} 
          shippingMethodSelected={!!watchedShippingMethod}
          appliedPoints={appliedPoints}
        />
        
        {/* Important Note Section */}
        <Paper
          variant="outlined"
          sx={{
            background: '#FCFAF6',
            border: '1px solid #E1DACA',
            p: 2,
            mb: 2,
            mt: 2,
            fontSize: 14,
            color: '#6B6B6B',
          }}
        >
          <div>
            -Please note that import duties and taxes are not included in the
            item's price. If incurred, these additional charges are the
            customer's responsibility.
            <br />
            <br />
            -If packages are returned to Hatsuhiken due to an incorrect or
            incomplete address, or if the recipient is unavailable, they will
            not be refundable. Please double-check your zip and detailed
            address before checkout to avoid this issue.
            <br />
            <br />
            -Modifications to your order can only be made before payment. Once
            your payment has been processed, no changes can be made.
          </div>
        </Paper>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <Button
            type="submit"
            variant="contained"
            color="inherit"
            size="large"
            fullWidth
            disabled={Boolean(isProcessing || (isStripeSelected && !cardComplete))}
            sx={{
              bgcolor: '#b48b6a',
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
              '&:disabled': {
                bgcolor: '#ccc',
              },
            }}
          >
            {isProcessing ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon sx={{ fontSize: 20 }} />
                {t('checkout.placeOrder')} & Pay
              </Box>
            )}
          </Button>
        </form>
      </Box>
      {/* Country Selection Dialog */}
      <CountrySelectionDialog
        open={countrySelectionDialogOpen}
        onClose={() => setCountrySelectionDialogOpen(false)}
        selectedCountry={selectedCountry}
        onSelectCountry={handleSelectCountry}
        selectedMarketId={selectedCountry?.marketId}
      />
    </Box>
  );
};

export default Checkout;

